import { ViewportProvider } from '@lightspeed/design-system-react'
import React, { FunctionComponent, ReactElement, useRef } from 'react'
import { SsboGuidedTourTooltip } from './SsboGuidedTourTooltip'
import { SsboTourStepId } from './ssbo-tour-steps'
import { useSsboTour } from './useSsboTour'

export interface SsboTourStepProps {
  /** Which step this host is (the tour shows it while its screen is mounted). */
  stepId: SsboTourStepId
  /** The anchor element (button) the pop-up tethers to. */
  children: ReactElement
  /** Only show the pop-up while the anchor is actionable. */
  enabled: boolean
}

/** Shows the tour pop-up around its child anchor while the tour is active and the anchor is enabled. */
export const SsboTourStep: FunctionComponent<SsboTourStepProps> = props => (
  // useSsboTour reads the viewport for the narrow-screen placement fallback.
  <ViewportProvider>
    <SsboTourStepContent {...props} />
  </ViewportProvider>
)

const SsboTourStepContent: FunctionComponent<SsboTourStepProps> = ({
  stepId,
  children,
  enabled,
}) => {
  const { isActive, step, currentStep, totalSteps, dismiss } = useSsboTour(
    stepId
  )

  // Once the tour has been active, keep the wrapper mounted and only toggle
  // the pop-up
  const hasBeenActive = useRef(isActive)
  if (isActive) {
    hasBeenActive.current = true
  }

  if (!step || !hasBeenActive.current) {
    return children
  }

  return (
    <SsboGuidedTourTooltip
      direction={step.placement}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onDismiss={dismiss}
      body={step.body}
      visible={isActive && enabled}
    >
      {children}
    </SsboGuidedTourTooltip>
  )
}
